import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { Compass, MessagesSquare, Server, Video } from 'lucide-react';
import type { SelfUser } from '@spawnwire/shared';
import { Wordmark } from '../../components/Logo';
import { Button, Field, Input } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { native } from '../../lib/native';
import { LEGAL } from '../legal/details';
import { actions } from '../../store/store';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const location = useLocation();
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isLogin = mode === 'login';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isLogin && !acceptTerms) {
      setError('Agree to the Terms of Service and Community Rules to create your account.');
      return;
    }
    setBusy(true);
    try {
      // The Android app gets its session token back (the website gets a cookie instead) and stores it first.
      const res = isLogin
        ? await http.post<{ user: SelfUser; token?: string }>('/auth/login', { login, password })
        : await http.post<{ user: SelfUser; token?: string }>('/auth/register', { email, username, password, displayName: displayName || undefined, acceptTerms });
      if (res.token) await native?.signedIn(res.token, res.user.id);
      actions.setMe(res.user);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full overflow-y-auto bg-rail">
      <aside className="relative hidden w-[44%] max-w-xl flex-col justify-between overflow-hidden bg-sidebar p-10 lg:flex">
        <Wordmark />
        <div className="space-y-6">
          <h1 className="text-4xl font-bold leading-tight tracking-tight text-fg">Find your community.</h1>
          <p className="text-muted">Meet people who play what you play, hang out with your friends, and spin up a game server together.</p>
          <ul className="space-y-4 text-muted">
            {[
              { icon: Compass, text: 'Discover communities for gaming, tech, chill hangouts and more.' },
              { icon: Server, text: 'Host a Game server with your friends, completely free.' },
              { icon: Video, text: 'Voice, video and screen sharing whenever you want to hang out.' },
              { icon: MessagesSquare, text: 'Message friends one-on-one or in group chats.' },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3">
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
                  <Icon className="size-4" aria-hidden />
                </span>
                <span className="pt-1.5 text-sm">{text}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Keeps the heading centred between the logo and the bottom edge. */}
        <div aria-hidden />

      </aside>

      <main className="flex flex-1 items-center justify-center p-4 sm:p-8">
        <form onSubmit={submit} className="animate-pop w-full max-w-sm space-y-5 rounded-2xl border border-line bg-main p-6 shadow-pop sm:p-8" noValidate>
          <div className="lg:hidden">
            <Wordmark />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-fg">{isLogin ? 'Welcome back' : 'Create your account'}</h2>
            <p className="mt-1 text-sm text-muted">{isLogin ? 'Sign in to pick up where you left off.' : 'It takes less than a minute.'}</p>
          </div>

          {isLogin ? (
            <Field label="Email or username" htmlFor="login">
              <Input id="login" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} required autoFocus />
            </Field>
          ) : (
            <>
              <Field label="Email" htmlFor="email">
                <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </Field>
              <Field label="Username" htmlFor="username" hint="Letters, numbers, periods and underscores.">
                <Input id="username" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} required minLength={2} maxLength={32} />
              </Field>
              <Field label="Display name" htmlFor="displayName" hint="Optional. Shown instead of your username.">
                <Input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={32} />
              </Field>
            </>
          )}
          <Field label="Password" htmlFor="password" hint={isLogin ? undefined : 'At least 8 characters.'}>
            <Input id="password" type="password" autoComplete={isLogin ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={isLogin ? 1 : 8} />
          </Field>

          {!isLogin && (
            <label className="flex items-start gap-3 text-sm text-muted">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5 size-4 shrink-0 accent-[var(--p-accent)]" />
              <span>
                I'm at least {LEGAL.minimumAge} and I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener" className="text-link hover:underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/rules" target="_blank" rel="noopener" className="text-link hover:underline">
                  Community Rules
                </a>
                . See how we handle your data in the{' '}
                <a href="/privacy" target="_blank" rel="noopener" className="text-link hover:underline">
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          )}

          {error && (
            <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" className="w-full" loading={busy}>
            {isLogin ? 'Sign in' : 'Create account'}
          </Button>
          <p className="text-center text-sm text-muted">
            {isLogin ? 'New here? ' : 'Already have an account? '}
            <Link to={(isLogin ? '/register' : '/login') + location.search} className="font-medium text-accent hover:underline">
              {isLogin ? 'Create an account' : 'Sign in'}
            </Link>
          </p>
          {isLogin && (
            <p className="text-center text-sm text-muted">
              Can't sign in?{' '}
              <Link to="/support" className="font-medium text-accent hover:underline">
                Contact support
              </Link>
            </p>
          )}
          <p className="flex justify-center gap-4 text-xs text-faint">
            <Link to="/rules" className="hover:text-fg">
              Community Rules
            </Link>
            <Link to="/terms" className="hover:text-fg">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-fg">
              Privacy
            </Link>
          </p>
        </form>
      </main>
    </div>
  );
}
